<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import ScimSettingsAPI from 'dashboard/api/scimSettings';
import SectionLayout from '../../account/components/SectionLayout.vue';
import Switch from 'next/switch/Switch.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();

const loading = ref(true);
const isEnabled = ref(false);
const baseUrl = ref('');
const tokenConfigured = ref(false);
const tokenHint = ref('');
const tokenRotatedAt = ref(null);
const plaintextToken = ref('');
const busy = ref(false);

const infoItems = computed(() => {
  const items = [
    {
      key: 'BASE_URL',
      label: t('SECURITY_SETTINGS.SCIM.BASE_URL.LABEL'),
      value: baseUrl.value,
      tooltip: t('SECURITY_SETTINGS.SCIM.BASE_URL.TOOLTIP'),
      show: !!baseUrl.value,
    },
  ];

  if (plaintextToken.value) {
    items.push({
      key: 'TOKEN',
      label: t('SECURITY_SETTINGS.SCIM.TOKEN.LABEL'),
      value: plaintextToken.value,
      tooltip: t('SECURITY_SETTINGS.SCIM.TOKEN.HELP'),
      show: true,
    });
  }

  return items.filter(item => item.show);
});

const load = async () => {
  loading.value = true;
  try {
    const { data } = await ScimSettingsAPI.get();
    applyData(data);
    isEnabled.value = Boolean(data.enabled);
  } catch {
    useAlert(t('SECURITY_SETTINGS.SCIM.API.ERROR_LOADING'));
  } finally {
    loading.value = false;
  }
};

const applyData = data => {
  baseUrl.value = data.base_url || '';
  tokenConfigured.value = Boolean(data.token_configured);
  tokenHint.value = data.token_hint || '';
  tokenRotatedAt.value = data.token_rotated_at || null;
  if (data.plaintext_token) {
    plaintextToken.value = data.plaintext_token;
  }
};

const handleCopy = async text => {
  await copyTextToClipboard(text);
  useAlert(t('SECURITY_SETTINGS.SCIM.COPY_SUCCESS'));
};

const generateOrRotate = async () => {
  busy.value = true;
  plaintextToken.value = '';
  try {
    const { data } = tokenConfigured.value
      ? await ScimSettingsAPI.rotate()
      : await ScimSettingsAPI.create();
    applyData(data);
    isEnabled.value = true;
    useAlert(t('SECURITY_SETTINGS.SCIM.API.TOKEN_GENERATED'));
  } catch {
    useAlert(t('SECURITY_SETTINGS.SCIM.API.ERROR'));
  } finally {
    busy.value = false;
  }
};

const revoke = async () => {
  busy.value = true;
  try {
    const { data } = await ScimSettingsAPI.revoke();
    plaintextToken.value = '';
    applyData(data);
    useAlert(t('SECURITY_SETTINGS.SCIM.API.TOKEN_REVOKED'));
  } catch {
    useAlert(t('SECURITY_SETTINGS.SCIM.API.ERROR'));
  } finally {
    busy.value = false;
  }
};

const handleDisable = async () => {
  busy.value = true;
  try {
    await ScimSettingsAPI.delete();
    plaintextToken.value = '';
    tokenConfigured.value = false;
    tokenHint.value = '';
    tokenRotatedAt.value = null;
    isEnabled.value = false;
    useAlert(t('SECURITY_SETTINGS.SCIM.API.DISABLED'));
  } catch {
    isEnabled.value = true;
    useAlert(t('SECURITY_SETTINGS.SCIM.API.ERROR'));
  } finally {
    busy.value = false;
  }
};

// Same pattern as SAML: turning on only reveals the form; turning off deletes settings.
const toggleScim = async () => {
  if (!isEnabled.value) {
    await handleDisable();
  }
};

onMounted(load);
</script>

<template>
  <SectionLayout
    :title="t('SECURITY_SETTINGS.SCIM.TITLE')"
    :description="t('SECURITY_SETTINGS.SCIM.NOTE')"
    beta
    :hide-content="!isEnabled || loading"
    with-border
  >
    <template #headerActions>
      <div class="flex justify-end">
        <Switch
          v-model="isEnabled"
          :disabled="loading || busy"
          @change="toggleScim"
        />
      </div>
    </template>

    <div class="space-y-4 pt-5">
      <section
        v-if="infoItems.length"
        class="rounded-xl border border-n-weak bg-n-solid-1 w-full text-sm text-n-slate-12 divide-y divide-n-weak"
      >
        <div
          v-for="item in infoItems"
          :key="item.key"
          class="ps-4 pe-1 py-1 flex justify-between items-center gap-2"
        >
          <div class="flex items-center gap-2 min-w-0">
            <span class="text-n-slate-11 w-32 shrink-0 flex items-center gap-1">
              {{ item.label }}
              <i
                v-tooltip.top="item.tooltip"
                class="i-lucide-info text-n-slate-9 w-3 h-3 cursor-help"
              />
            </span>
            <span class="flex-1 truncate">{{ item.value }}</span>
          </div>
          <NextButton
            type="button"
            ghost
            sm
            slate
            icon="i-lucide-copy"
            @click="handleCopy(item.value)"
          />
        </div>
      </section>

      <div>
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('SECURITY_SETTINGS.SCIM.TOKEN.LABEL') }}
        </label>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ $t('SECURITY_SETTINGS.SCIM.TOKEN.HELP') }}
        </p>
        <p
          v-if="tokenConfigured && !plaintextToken"
          class="mt-2 text-sm text-n-slate-11"
        >
          {{ $t('SECURITY_SETTINGS.SCIM.TOKEN.HINT') }} …{{ tokenHint }}
          <span v-if="tokenRotatedAt">
            · {{ $t('SECURITY_SETTINGS.SCIM.TOKEN.LAST_ROTATED') }}:
            {{ tokenRotatedAt }}
          </span>
        </p>
        <p v-else-if="!plaintextToken" class="mt-2 text-sm text-n-slate-11">
          {{ $t('SECURITY_SETTINGS.SCIM.TOKEN.NONE') }}
        </p>
        <div class="mt-3 flex flex-wrap gap-2">
          <NextButton
            sm
            solid
            :is-loading="busy"
            :label="
              tokenConfigured
                ? $t('SECURITY_SETTINGS.SCIM.TOKEN.ROTATE')
                : $t('SECURITY_SETTINGS.SCIM.TOKEN.GENERATE')
            "
            @click="generateOrRotate"
          />
          <NextButton
            v-if="tokenConfigured"
            sm
            faded
            ruby
            :disabled="busy"
            :label="$t('SECURITY_SETTINGS.SCIM.TOKEN.REVOKE')"
            @click="revoke"
          />
        </div>
      </div>
    </div>
  </SectionLayout>
</template>
